/**
 * @fileoverview Active Clients screen state hook.
 * @description Owns what the Active Clients page shows: the grid's filters, sort and page, the
 * debounced search, the three queries (KPI tiles, filter choices, one page of the grid), and the
 * client whose dialogs are open. Search, filters, status and sort all go to the backend, so any
 * change returns the grid to page 1; a page past the end (the last row of the last page was
 * archived) steps back to the last one that exists.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

import { useClientFilterOptions } from '@/features/sales/hooks/useClientFilterOptions'
import { useClients } from '@/features/sales/hooks/useClients'
import { useClientSummary } from '@/features/sales/hooks/useClientSummary'
import {
  ALL_CLIENTS_FILTER,
  CLIENT_PAGE_SIZE,
  DEFAULT_CLIENT_SORT,
  nextSort,
  type ClientSort,
} from '@/features/sales/lib/sales-clients.utils'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

import type {
  ClientListFilters,
  SalesClientSortField,
  SalesClientStatus,
} from '@/features/sales/types/sales-client.types'

/** @description What a change to the grid's filters can set. */
export interface ClientFilterChange {
  search?: string
  stateId?: string
  executiveUserId?: string
  status?: SalesClientStatus
}

/** The filters as the person set them, before the search is debounced. */
interface FilterState {
  search: string
  stateId: string
  executiveUserId: string
  status: SalesClientStatus
}

const DEFAULT_FILTER_STATE: FilterState = {
  search: '',
  stateId: ALL_CLIENTS_FILTER,
  executiveUserId: ALL_CLIENTS_FILTER,
  status: 'ACTIVE',
}

/**
 * @description The Active Clients screen's state and queries.
 * @returns {object} The filters, sort and page with their setters, the three queries and the
 * client whose dialogs are open.
 */
export function useActiveClientsScreen() {
  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE)
  const [sort, setSort] = useState<ClientSort>(DEFAULT_CLIENT_SORT)
  const [page, setPage] = useState(1)
  const search = useDebouncedValue(filterState.search.trim())

  const filters: ClientListFilters = {
    search,
    stateId: filterState.stateId,
    executiveUserId: filterState.executiveUserId,
    status: filterState.status,
    sortBy: sort.field,
    sortDirection: sort.direction,
    page,
    limit: CLIENT_PAGE_SIZE,
  }
  const clientsQuery = useClients(filters)
  const summaryQuery = useClientSummary()
  const optionsQuery = useClientFilterOptions()
  const totalPages = clientsQuery.data?.meta.totalPages ?? 1

  if (clientsQuery.isSuccess && page > Math.max(totalPages, 1)) setPage(Math.max(totalPages, 1))

  /**
   * @description Applies a filter change and returns the grid to page 1.
   * @param {ClientFilterChange} change - The filters to set.
   */
  const changeFilters = (change: ClientFilterChange) => {
    setFilterState((current) => ({ ...current, ...change }))
    setPage(1)
  }

  /**
   * @description Resets every filter to its default and returns to page 1.
   */
  const clearFilters = () => {
    setFilterState(DEFAULT_FILTER_STATE)
    setPage(1)
  }

  /**
   * @description Sorts by a column: a second click on the same column reverses it.
   * @param {SalesClientSortField} field - The column to sort by.
   */
  const changeSort = (field: SalesClientSortField) => {
    setSort((current) => nextSort(current, field))
    setPage(1)
  }

  return {
    filterState,
    anyFilter:
      filterState.search.trim() !== '' ||
      filterState.stateId !== ALL_CLIENTS_FILTER ||
      filterState.executiveUserId !== ALL_CLIENTS_FILTER ||
      filterState.status !== 'ACTIVE',
    sort,
    page,
    setPage,
    changeFilters,
    clearFilters,
    changeSort,
    clientsQuery,
    summaryQuery,
    optionsQuery,
  }
}
